import React, { useEffect, useState } from 'react';
import { Briefcase, Search, X, Truck } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { crmJobsAPI } from '../lib/api';

const ALLOCATABLE = ['open', 'scheduled', 'in_progress'];
export const jobLabel = (j) => `#${j.job_number}${j.job_name ? ` — ${j.job_name}` : ''}`;

export const JobAllocationPicker = ({ value, onChange, disabled, vehicleSelected }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await crmJobsAPI.list({ search: query.trim(), page_size: 100 });
        setResults((res.data.jobs || []).filter(j => ALLOCATABLE.includes((j.status || '').toLowerCase())));
      } catch { setResults([]); } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  if (vehicleSelected) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-dashed p-3 text-xs text-muted-foreground" data-testid="po-assign-job-vehicle-note">
        <Truck className="mt-0.5 h-4 w-4 shrink-0" />
        Job allocation is only available when receiving into a warehouse. Stock received into a vehicle stays unallocated.
      </div>
    );
  }
  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-primary/40 bg-primary/5 p-3" data-testid="po-assign-job-selected">
        <div className="flex min-w-0 items-center gap-2 text-sm">
          <Briefcase className="h-4 w-4 shrink-0 text-primary" />
          <span className="truncate font-medium">{jobLabel(value)}</span>
        </div>
        <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => onChange(null)}
          aria-label="Clear job" data-testid="po-assign-job-clear-btn"><X className="h-4 w-4" /></Button>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} disabled={disabled} className="pl-9"
          placeholder="Search job number or name (optional)" aria-label="Search jobs" data-testid="po-assign-job-search" />
      </div>
      {searching && <p className="px-1 text-xs text-muted-foreground">Searching…</p>}
      {!searching && query.trim().length >= 2 && results.length === 0 && (
        <p className="px-1 text-xs text-muted-foreground" data-testid="po-assign-job-no-results">No open, scheduled or in-progress jobs match.</p>
      )}
      {results.length > 0 && (
        <ul className="max-h-48 divide-y overflow-y-auto rounded-lg border" role="listbox" data-testid="po-assign-job-results">
          {results.map(j => (
            <li key={j.id}>
              <button type="button" role="option" aria-selected="false" data-testid={`po-assign-job-option-${j.id}`}
                onClick={() => { onChange({ id: j.id, job_number: j.job_number, job_name: j.job_name }); setQuery(''); }}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted focus:bg-muted focus:outline-none">
                <span className="truncate">{jobLabel(j)}</span>
                <span className="shrink-0 text-xs capitalize text-muted-foreground">{(j.status || '').replace('_', ' ')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
